import {
	type EmailSettingsRequest,
	type EmailSettingsResponse,
	type NotificationToggle,
	type NotificationToggleInput,
	type NotificationToggleList,
	type SenderDomainView,
	TenantConfigPlane,
} from '@today/types'
import { apiClient } from './api.client'

/**
 * The route tree a console plane addresses (TO-382; TO-632 unified settings
 * tree): the tenant plane is slug-keyed `/t/{slug}/admin/settings`, the platform
 * plane is `/admin/tenants/{tenantId}/settings`. The `toggles` section is now
 * `notifications`.
 */
const baseOf = (tenantId: string, tenantSlug: string, plane: TenantConfigPlane) =>
	plane === TenantConfigPlane.PLATFORM
		? `/admin/tenants/${encodeURIComponent(tenantId)}/settings`
		: `/t/${encodeURIComponent(tenantSlug)}/admin/settings`

/** A tenant's email sender settings and notification toggles (TO-382). */
export const notificationSettingsService = {
	/** `GET …/settings/email` — binding, sending domain, effective sender and warnings. */
	async getEmail(tenantId: string, tenantSlug: string, plane: TenantConfigPlane): Promise<EmailSettingsResponse> {
		const { data } = await apiClient.get<EmailSettingsResponse>(`${baseOf(tenantId, tenantSlug, plane)}/email`)
		return data
	},

	/** `PUT …/settings/email` — replace the binding; `credentials` are write-only. */
	async putEmail(
		tenantId: string,
		tenantSlug: string,
		plane: TenantConfigPlane,
		body: EmailSettingsRequest,
	): Promise<EmailSettingsResponse> {
		const { data } = await apiClient.put<EmailSettingsResponse>(`${baseOf(tenantId, tenantSlug, plane)}/email`, body)
		return data
	},

	/** `DELETE …/settings/email` — back to the platform sender. */
	async deleteEmail(tenantId: string, tenantSlug: string, plane: TenantConfigPlane): Promise<void> {
		await apiClient.delete(`${baseOf(tenantId, tenantSlug, plane)}/email`)
	},

	/** `POST …/settings/email/domain-verification` — register or re-check the sending domain. */
	async verifyDomain(tenantId: string, tenantSlug: string, plane: TenantConfigPlane): Promise<SenderDomainView> {
		const { data } = await apiClient.post<SenderDomainView>(
			`${baseOf(tenantId, tenantSlug, plane)}/email/domain-verification`,
		)
		return data
	},

	/** `GET …/settings/notifications` — every notification type × channel. */
	async getToggles(tenantId: string, tenantSlug: string, plane: TenantConfigPlane): Promise<NotificationToggleList> {
		const { data } = await apiClient.get<NotificationToggleList>(`${baseOf(tenantId, tenantSlug, plane)}/notifications`)
		return data
	},

	/** `PUT …/settings/notifications` — store the listed pairs; returns the full catalog. */
	async putToggles(
		tenantId: string,
		tenantSlug: string,
		plane: TenantConfigPlane,
		items: NotificationToggleInput[],
	): Promise<NotificationToggle[]> {
		const { data } = await apiClient.put<NotificationToggleList>(
			`${baseOf(tenantId, tenantSlug, plane)}/notifications`,
			{ items },
		)
		return data.items
	},
}
